<template>
  <ActionsMenu
    :actions="actions"
    :options="actionMenuOptions"
    :tech-mode="techMode"
    @approve-quote="emitEvent('quote-approve-click', 'quote_accept')"
    @force-approve-quote="
      emitEvent('quote-force-approve-click', 'quote_force_accept')
    "
    @schedule-work="emitEvent('schedule-work-click', 'quote_schedule')"
    @decline-quote="emitEvent('decline-quote-click', 'quote_decline')"
    @draft-quote="emitEvent('draft-quote-click', 'quote_draft')"
    @pending-quote="emitEvent('pending-quote-click', 'quote_pending')"
    @edit-quote="emitEvent('edit-quote-click')"
    @email-quote="emitEvent('email-quote-click', 'quote_email')"
    @pdf-quote="emitEvent('pdf-quote-click', 'quote_pdf')"
    @archive-quote="emitEvent('archive-quote-click', 'quote_archive')"
    @unarchive-quote="emitEvent('unarchive-quote-click', 'quote_unarchive')"
    @clone-quote="emitEvent('clone-quote-click', 'quote_clone')"
    @reactivate-quote="emitEvent('reactivate-quote-click', 'quote_reactivate')"
    @unapprove-quote="emitEvent('unapprove-quote-click', 'quote_break')"
  >
    <template #trigger>
      <slot name="trigger">
        <EllipsisIcon class="tooltip-trigger" width="20" />
      </slot>
    </template>
    <template #footer><slot name="footer"></slot></template>
  </ActionsMenu>
</template>

<script>
import { EventBus } from "@/lib/event_bus";

import ActionsMenu from "@/components/ActionsMenu.vue";
import EllipsisIcon from "@/images/dots-horizontal-outline.svg?component";

export default {
  name: "QuoteActionMenu",
  components: {
    ActionsMenu,
    EllipsisIcon
  },
  props: {
    quote: {
      type: Object,
      default: () => ({})
    },
    techMode: {
      type: Boolean,
      default: false
    },
    tippyOptions: {
      type: Object,
      default: () => ({})
    },
    quoteGroupMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    actionMenuOptions() {
      // tippy should be appended to the parent
      // instead of body when used as webcomponent in tech app
      // to handle styling the tooltip within the shadow dom
      return {
        ...this.tippyOptions,
        theme: "light",
        appendTo: this.techMode ? "parent" : document.body
      };
    },
    actions() {
      // Groups (accept → status → edit → share → lifecycle) match the
      // sibling QuoteActionsMenu in components/Quote/. ActionsMenu draws
      // a 1px divider whenever `group` differs between adjacent items.
      // Order within each group preserves common-case workflow flow.
      return [
        // accept — move the quote forward in its workflow
        {
          label: "Propose",
          icon: "fa-regular fa-check-circle",
          eventName: "pending-quote",
          className: "menu-item",
          disabled: !this.quote.links.quote_pending,
          visible: this.quote.status === "draft",
          group: "accept"
        },
        {
          label: "Create an Invoice",
          icon: "fa-regular fa-file-invoice-dollar",
          eventName: "approve-quote",
          className: "menu-item",
          visible: !this.quote.job_id && !this.quoteGroupMode && !this.techMode,
          disabled: !this.quote.links.quote_accept,
          group: "accept"
        },
        {
          label: "Manually Accept Quote",
          icon: "fa-regular fa-thumbs-up",
          eventName: "approve-quote",
          className: "menu-item",
          visible:
            (this.quote.job_id || this.quoteGroupMode) &&
            !this.techMode &&
            !this.quote.links.quote_force_accept,
          disabled: !this.quote.links.quote_accept,
          group: "accept"
        },
        {
          label: "Force Accept",
          icon: "fa-regular fa-thumbs-up",
          eventName: "force-approve-quote",
          className: "menu-item",
          visible: this.quote.links.quote_force_accept && !this.techMode,
          disabled: !this.quote.links.quote_force_accept,
          group: "accept"
        },
        {
          label: "Schedule Work",
          icon: "fa-regular fa-calendar-alt",
          eventName: "schedule-work",
          className: "menu-item",
          visible: !this.quote.job_id && !this.quoteGroupMode && !this.techMode,
          disabled: !this.quote.links.quote_schedule,
          group: "accept"
        },
        // status — transitions between proposed/denied/accepted
        {
          label: "Reactivate",
          icon: "fa-regular fa-check-circle",
          eventName: "reactivate-quote",
          className: "menu-item",
          disabled: !this.quote.links.quote_reactivate,
          visible: this.quote.status === "denied",
          group: "status"
        },
        {
          label: "Draft",
          icon: "fa-regular fa-file-edit",
          eventName: "draft-quote",
          className: "menu-item",
          disabled: !this.quote.links.quote_draft,
          visible: !["draft", "denied"].includes(this.quote.status),
          group: "status"
        },
        {
          label: "Decline Quote",
          icon: "fa-regular fa-thumbs-down",
          eventName: "decline-quote",
          className: "menu-item",
          disabled: !this.quote.links.quote_decline,
          visible: !["draft", "denied"].includes(this.quote.status),
          group: "status"
        },
        {
          label: "Unapprove",
          icon: "fa-regular fa-times-hexagon",
          eventName: "unapprove-quote",
          className: "menu-item",
          visible: this.quote.links.quote_break,
          group: "status"
        },
        // edit — content + duplication
        {
          label: "Edit",
          icon: "fa-regular fa-edit",
          eventName: "edit-quote",
          className: "menu-item",
          disabled: !this.quote.links.quote_update,
          group: "edit"
        },
        {
          label: "Edit Details",
          icon: "fa-regular fa-edit",
          eventName: "edit-details-click",
          className: "menu-item",
          disabled: !this.quote.links.update_details,
          visible: !this.techMode,
          group: "edit"
        },
        {
          label: "Clone",
          icon: "fa-regular fa-copy",
          eventName: "clone-quote",
          className: "menu-item",
          disabled: !this.quote.links.quote_clone,
          group: "edit"
        },
        // share — distribute the quote to the customer
        {
          label: "Email",
          icon: "fa-regular fa-envelope",
          eventName: "email-quote",
          className: "menu-item",
          disabled: !this.quote.links.quote_email,
          visible: this.quote.status !== "draft",
          group: "share"
        },
        {
          label: "Download PDF",
          icon: "fa-regular fa-file-pdf",
          eventName: "pdf-quote",
          className: "menu-item",
          disabled: !this.quote.links.quote_pdf,
          visible: !this.techMode,
          group: "share"
        },
        // lifecycle — archive / unarchive
        {
          label: "Archive",
          icon: "fa-regular fa-archive",
          eventName: "archive-quote",
          className: "menu-item",
          visible: !this.quote.archived,
          disabled: !this.quote.links.quote_archive,
          group: "lifecycle"
        },
        {
          label: "Unarchive",
          icon: "fa-regular fa-box-open",
          eventName: "unarchive-quote",
          className: "menu-item",
          visible: this.quote.archived,
          disabled: !this.quote.links.quote_unarchive,
          group: "lifecycle"
        }
      ];
    }
  },
  methods: {
    emitEvent(eventName, linkName) {
      const payload = {
        id: this.quote.id,
        link: this.quote.links[linkName]
      };
      if (this.quoteGroupMode) {
        EventBus.$emit(eventName, payload);
      } else {
        this.$emit(eventName, payload);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.action-trigger {
  font-size: 1em;
  box-sizing: content-box;
  padding: 7px;
}
</style>
